import React, { useEffect, useRef } from 'react';
import { ArrowRight, Star } from 'lucide-react';
import Button from '../atoms/Button';
import { motion } from 'framer-motion';
import gsap from 'gsap';

const Hero = () => {
    const cardRef = useRef(null);

    useEffect(() => {
        const card = cardRef.current;
        if (card) {
            gsap.to(card, {
                y: -15,
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: "power1.inOut"
            });
        }
    }, []);

    return (
        <section id="home" className="relative pt-24 pb-12 lg:pb-16">
            <div className="container mx-auto px-6 h-full">
                <div className="flex flex-col lg:flex-row items-center gap-12 h-full">

                    {/* Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="lg:w-1/2 space-y-8 py-12"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-lavender-light text-purple-900 text-[10px] font-bold uppercase tracking-widest"
                        >
                            <span className="w-2 h-2 rounded-full bg-purple-600"></span> New Collection Drop
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.8 }}
                            className="serif text-6xl md:text-7xl lg:text-8xl leading-[1.1] text-gray-900"
                        >
                            Sculpted to <br /> <span className="italic text-purple-900/80">Perfection.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6, duration: 0.8 }}
                            className="text-lg text-gray-600 max-w-md font-light leading-relaxed"
                        >
                            Professional nail care and stunning 3D artistry designs for every occasion. From classic manicures to hold, trend-setting art.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.6 }}
                            className="flex flex-wrap gap-4 pt-4"
                        >
                            <Button onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}>
                                Book Appointment
                            </Button>
                            <Button
                                variant="outline"
                                className="flex items-center gap-2"
                                onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
                            >
                                View Gallery <ArrowRight className="w-4 h-4" />
                            </Button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1, duration: 0.8 }}
                            className="flex items-center gap-4 pt-8 border-t border-gray-200 mt-8"
                        >
                            <div className="flex -space-x-4">
                                <img className="w-10 h-10 rounded-full border-2 border-white object-cover" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100" alt="Client" />
                                <img className="w-10 h-10 rounded-full border-2 border-white object-cover" src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=100" alt="Client" />
                                <img className="w-10 h-10 rounded-full border-2 border-white object-cover" src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=100" alt="Client" />
                            </div>
                            <div className="text-sm">
                                <div className="flex text-yellow-500 mb-0.5">
                                    <Star className="w-3 h-3 fill-current" />
                                    <Star className="w-3 h-3 fill-current" />
                                    <Star className="w-3 h-3 fill-current" />
                                    <Star className="w-3 h-3 fill-current" />
                                    <Star className="w-3 h-3 fill-current" />
                                </div>
                                <p className="text-gray-500 font-medium">Trusted by 1000+ Clients</p>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Visual Content */}
                    <div className="lg:w-1/2 relative h-full w-full">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className="relative rounded-[2rem] overflow-hidden shadow-2xl max-h-[500px] lg:max-h-[600px]"
                        >
                            <img src="https://images.unsplash.com/photo-1632345031435-8727f6897d53?auto=format&fit=crop&q=80&w=1000" alt="Luxury Nail Art" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />

                            {/* Floating Card */}
                            <div ref={cardRef} className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-lg border border-white/50">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="serif text-xl font-bold">The "Rich Tran" Signature</h3>
                                        <p className="text-sm text-gray-600 mt-1">Structured Gel-X • 3D Charms</p>
                                    </div>
                                    <span className="bg-black text-white px-3 py-1 rounded-full text-xs font-bold">$145+</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default Hero;
